<template>
  <section class="value-section">
    <div class="container">
      <h2 class="section-title">
        {{ $t('home.coreFeatures') }}
      </h2>
      <p class="section-subtitle">
        {{ $t('home.comprehensiveSolution') }}
      </p>

      <div class="value-cards">
        <ValueCard
          v-for="(item, index) in valueCards"
          :key="index"
          :icon="item.icon"
          :title="item.title"
          :description="item.description"
          :action-text="item.actionText"
          @action-click="handleActionClick(item.action)"
        />
      </div>
    </div>
  </section>
</template>

<script lang="ts">
import ValueCard from '@/components/home/ValueCard.vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { ValueCardItem } from '../../api/base'
import { getCurrentInstance, ref, watchEffect } from 'vue'
export default {
  components: {
    ValueCard
  },
  setup () {
    const { t } = useI18n()
    const router = useRouter()
    const valueCards = ref<ValueCardItem[]>([])
    // 当语言或 t 函数变化时，重新计算 valueCards
    watchEffect(() => {
      valueCards.value = [
        {
          icon: 'certificate',
          title: t('home.oneClickVerification'), // '一键鉴伪',
          description: t('home.oneClickVerificationDesc'), // '扫描产品防伪码，快速验证产品真伪，保护您的消费权益',
          actionText: t('home.verifyNow'), // '立即验证',
          action: 'verification'
        },
        {
          icon: 'shield-o',
          title: t('home.anonymousReport'), // '匿名举报',
          description: t('home.anonymousReportDesc'), // '发现假冒产品可安全匿名举报，共同维护市场秩序与品牌声誉',
          actionText: t('home.reportNow'), // '我要举报',
          action: 'report'
        },
        {
          icon: 'calendar-o',
          title: t('home.industryCalendar'), // '行业日历',
          description: t('home.industryCalendarDesc'), // '掌握品牌最新动态、活动安排与行业资讯，不错过重要信息',
          actionText: t('home.viewCalendar'), // '查看日历',
          action: 'calendar'
        }
      ]
    })

    const handleActionClick = (action: string) => {
      switch (action) {
        case 'verification':
          router.push('/verification')
          break
        case 'report':
          router.push('/report')
          break
        case 'calendar':
          router.push('/calendar')
          break
      }
    }

    return {
      valueCards,
      handleActionClick
    }
  }
}

</script>

<style lang="scss" scoped>
@import '@/styles/variables.scss';

.value-section {
  padding: $spacing-xxl 0;
  background: linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 1px;
    background: linear-gradient(90deg, transparent, $accent-gold, transparent);
  }
}

.section-title {
  text-align: center;
  font-size: $font-size-xxl;
  font-weight: 700;
  margin-bottom: $spacing-md;
  color: $primary-blue;
  position: relative;

  &::after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background: $accent-gold;
    margin: $spacing-md auto;
    border-radius: 2px;
  }
}

.section-subtitle {
  text-align: center;
  font-size: $font-size-lg;
  color: $text-light;
  margin-bottom: $spacing-xxl;
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.value-cards {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: $spacing-xl;
  position: relative;
  //z-index: 2;
}

// 1080×1920 设备适配 (竖屏大屏设备)
@media (min-width: 1080px) and (min-height: 1920px) {
  .value-section {
    padding: $spacing-xxl * 1.5 0;
  }

  .section-title {
    font-size: 2.8rem;
    margin-bottom: $spacing-lg;

    &::after {
      width: 80px;
      height: 4px;
      margin: $spacing-lg auto;
    }
  }

  .section-subtitle {
    font-size: $font-size-xl;
    margin-bottom: $spacing-xxl * 1.5;
    max-width: 800px;
  }

  .value-cards {
    grid-template-columns: 1fr;
    gap: $spacing-xxl;
    max-width: 800px;
    margin: 0 auto;
    //min-width: 400px;
  }
}

// 大型桌面设备 (1440px 及以上)
@media (min-width: 1440px) {
  .value-section {
    padding: $spacing-xxl * 2 0;
  }

  .value-cards {
    gap: $spacing-xxl;
  }
}

// 平板设备 (768px - 1199px)
@media (max-width: 1199px) and (min-width: 768px) {
  .value-cards {
    grid-template-columns: repeat(2, 1fr);
    gap: $spacing-lg;
  }

  // 让第三个卡片占据整行
  .value-cards :nth-child(3) {
    grid-column: span 2;
    max-width: 500px;
    margin: 0 auto;
  }
}

// 移动端适配 (767px 及以下)
@media (max-width: 767px) {
  .value-section {
    padding: $spacing-xl 0;
  }

  .section-title {
    font-size: $font-size-xl;
    margin-bottom: $spacing-sm;
  }

  .section-subtitle {
    font-size: $font-size-md;
    margin-bottom: $spacing-xl;
    padding: 0 $spacing-md;
  }

  .value-cards {
    grid-template-columns: 1fr;
    gap: $spacing-lg;
    padding: 0 $spacing-md;
  }
}

// 超小屏幕设备 (480px 及以下)
@media (max-width: 480px) {
  .section-title {
    font-size: $font-size-lg;
  }

  .section-subtitle {
    font-size: $font-size-sm;
  }

  .value-cards {
    padding: 0 $spacing-sm;
  }
}

// 横屏模式适配
@media (max-height: 500px) and (orientation: landscape) {
  .value-section {
    padding: $spacing-xl 0;
  }

  .value-cards {
    grid-template-columns: repeat(3, 1fr);
    gap: $spacing-md;
  }
}

// 暗色模式支持
@media (prefers-color-scheme: dark) {
  .value-section {
    background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  }

  .section-title {
    color: $white;
  }

  .section-subtitle {
    color: rgba($white, 0.7);
  }
}
</style>
